import { AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"

interface GoNoGoScoringErrorProps {
  /**
   * `failed` — the run is over and nothing was saved.
   * `still_running` — we stopped waiting, but the server may yet finish.
   */
  kind: 'failed' | 'still_running'
  message: string
  onRetry: () => void
  onRefresh: () => void
}

/**
 * The visible half of a scoring run that ended badly.
 *
 * Before this existed the store recorded the failure and no Go/No-Go component
 * read it, so a failed run — or one the browser stopped waiting for — simply
 * made the spinner disappear. Two prod runs were lost that way on 2026-08-17,
 * one of which had actually succeeded three seconds after the client gave up.
 *
 * The two kinds get different actions on purpose: re-running a `still_running`
 * job would discard a result that is about to land, and cost another ~$0.20 and
 * ~3 minutes.
 */
export function GoNoGoScoringError({ kind, message, onRetry, onRefresh }: GoNoGoScoringErrorProps) {
  const isFailure = kind === 'failed'
  return (
    <div
      // A dead run is an alert; "still scoring" is only a status. Both are
      // announced, but the polite one must not interrupt.
      role={isFailure ? 'alert' : 'status'}
      className="flex items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-[hsl(var(--destructive))]"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      {/* `truncate` because `message` can be a raw API error — unbounded text
          here would wrap and grow the whole actions bar. */}
      <span className="min-w-0 truncate" title={message}>{message}</span>
      {isFailure ? (
        <Button variant="ghost" size="sm" className="shrink-0" onClick={onRetry}>
          Try again
        </Button>
      ) : (
        <Button variant="ghost" size="sm" className="shrink-0" onClick={onRefresh}>
          Check again
        </Button>
      )}
    </div>
  )
}
